/**
 * Where the realm map's name plates and world names sit on screen. Each
 * one belongs to a spot on the painting, but the screen has controls over
 * it (the top bar, the social buttons, the zoom buttons, the world
 * switcher), edges that cut things off, and other names. So a name slides
 * fully on screen, then moves the least it must to clear the controls and
 * the names already placed, and hides when its spot leaves the screen or
 * it would have to move too far. Pure functions, so the placing is tested
 * without a browser.
 */

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function overlaps(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/** The smallest rectangle holding all of `rects`. */
export function union(rects: readonly Rect[]): Rect | null {
  if (rects.length === 0) return null;
  const x = Math.min(...rects.map((r) => r.x));
  const y = Math.min(...rects.map((r) => r.y));
  const right = Math.max(...rects.map((r) => r.x + r.w));
  const bottom = Math.max(...rects.map((r) => r.y + r.h));
  return { x, y, w: right - x, h: bottom - y };
}

/** `r` grown by `by` on every side. */
export function pad(r: Rect, by: number): Rect {
  return { x: r.x - by, y: r.y - by, w: r.w + 2 * by, h: r.h + 2 * by };
}

const EPS = 0.5;

function inside(r: Rect, area: Rect): boolean {
  return (
    r.x >= area.x - EPS &&
    r.y >= area.y - EPS &&
    r.x + r.w <= area.x + area.w + EPS &&
    r.y + r.h <= area.y + area.h + EPS
  );
}

/** `r` slid the least that puts it inside `area` (centred when too big). */
export function clampInto(r: Rect, area: Rect): Rect {
  const x =
    r.w >= area.w
      ? area.x + (area.w - r.w) / 2
      : Math.min(Math.max(r.x, area.x), area.x + area.w - r.w);
  const y =
    r.h >= area.h
      ? area.y + (area.h - r.h) / 2
      : Math.min(Math.max(r.y, area.y), area.y + area.h - r.h);
  return { ...r, x, y };
}

/** How far `r` has moved from `from`. */
export function shiftOf(r: Rect, from: Rect): number {
  return Math.hypot(r.x - from.x, r.y - from.y);
}

/**
 * Move `r` the least that puts it inside `area` and clear of every rect in
 * `keepOut`. Null when there's no such place next to the control it hits.
 */
export function fitRect(r: Rect, area: Rect, keepOut: readonly Rect[]): Rect | null {
  let best = clampInto(r, area);
  for (let i = 0; i < 6; i++) {
    const hit = keepOut.find((k) => overlaps(best, k));
    if (!hit) return best;
    const moves = [
      { ...best, y: hit.y - best.h },
      { ...best, y: hit.y + hit.h },
      { ...best, x: hit.x - best.w },
      { ...best, x: hit.x + hit.w },
    ]
      .filter((m) => inside(m, area))
      .sort((a, b) => shiftOf(a, r) - shiftOf(b, r));
    if (moves.length === 0) return null;
    best = moves.find((m) => !keepOut.some((k) => overlaps(m, k))) ?? moves[0]!;
  }
  return keepOut.some((k) => overlaps(best, k)) ? null : best;
}

/** Whether a spot on the painting is on screen. */
export function spotOnScreen(
  spot: { x: number; y: number },
  view: { w: number; h: number },
): boolean {
  return spot.x >= 0 && spot.y >= 0 && spot.x <= view.w && spot.y <= view.h;
}

/** A name to place: where it would sit by its spot, on screen. */
export interface LabelInput {
  want: Rect;
  spot: { x: number; y: number };
  /** The furthest it may move to clear the controls and other names. */
  maxShift: number;
  /** A building's plate slides fully on screen for free while its spot is
   *  on screen, so it stays readable; a world's name counts that slide
   *  against `maxShift`, so it never drifts far from its world. */
  slide: boolean;
  /** Lower groups are placed first and keep their places: the building
   *  plates (0) before the world names (1). */
  group: number;
}

export interface LabelPlace {
  at: Rect;
  /** Shown, or hidden (the map fades it out) because its spot is off
   *  screen or it would have to move too far. */
  shown: boolean;
}

/**
 * Place every name: on screen, clear of `keepOut`, and clear of the names
 * shown before it. Within a group, the names that move least go first,
 * so a name sitting by its spot keeps it and a displaced one goes round.
 */
export function placeLabels(
  items: readonly LabelInput[],
  view: { w: number; h: number },
  keepOut: readonly Rect[],
  edge: number,
): LabelPlace[] {
  const area = { x: edge, y: edge, w: view.w - 2 * edge, h: view.h - 2 * edge };
  const first = items.map((it) => {
    const onScreen = clampInto(it.want, area);
    const from = it.slide ? onScreen : it.want;
    const at = fitRect(onScreen, area, keepOut);
    return { onScreen, from, at, shift: at ? shiftOf(at, from) : Infinity };
  });
  const order = items
    .map((_, i) => i)
    .sort((a, b) => items[a]!.group - items[b]!.group || first[a]!.shift - first[b]!.shift);
  const placed: Rect[] = [];
  const out: LabelPlace[] = items.map((it) => ({ at: it.want, shown: false }));
  for (const i of order) {
    const it = items[i]!;
    const { onScreen, from } = first[i]!;
    let at = first[i]!.at;
    if (at && placed.some((p) => overlaps(p, at!))) {
      at = fitRect(onScreen, area, [...keepOut, ...placed]);
    }
    const shown = !!at && spotOnScreen(it.spot, view) && shiftOf(at, from) <= it.maxShift;
    out[i] = { at: at ?? onScreen, shown };
    if (at && shown) placed.push(pad(at, 2));
  }
  return out;
}
